import React, {useMemo} from 'react';

import {AsyncDataLoader} from '../../components/common/loaders/asyncDataLoader';
import {useLinkNavigation} from '../../components/common/navigation/useLinkNavigation';
import {useAppSelector} from '../../helpers/reduxHelpers';
import {SettingsResourcePagesEnum, SettingsTeammateUrl} from '../../models/url/settingsUrlModel';
import {getFlatTeammates} from '../../store/data/teammates/teammatesSelectors';
import {SettingsPanelId} from '../../store/panels/panelStateBase';
import {SettingsBannersProvider} from '../banners/settingsBannersProvider';
import {buildRuleEditorBreadcrumbsForOtherTeammate} from '../rules/ruleEditor/ruleEditorBreadcrumbs';
import {
  createRuleEditorServiceContext,
  RuleEditorServiceContext,
} from '../rules/ruleEditor/ruleEditorServiceContext';
import {RuleEditorWrapper} from '../rules/ruleEditorWrapper';
import {useRuleIntegrations} from '../rules/ruleIntegrationModel';

/*
 * Types.
 */

interface SettingsTeammateEditRuleProps {
  panelId: SettingsPanelId;
  teammateAlias: string;
  namespace: string;
  subResourcePage: SettingsResourcePagesEnum;
  ruleId?: number;
  url: SettingsTeammateUrl;
}

/*
 * Component.
 */

export const SettingsTeammateEditRule = (props: SettingsTeammateEditRuleProps) => {
  const {panelId, teammateAlias, subResourcePage, namespace, ruleId} = props;

  const editorServiceContext = useMemo(() => createRuleEditorServiceContext(), []);
  const ruleIntegrationsAsyncData = useRuleIntegrations();
  const teammates = useAppSelector(getFlatTeammates);
  const {navigateToLink} = useLinkNavigation();

  const ruleEditorTeammate = useMemo(
    () => teammates.find((teammate) => teammate.alias === teammateAlias),
    [teammates, teammateAlias],
  );

  const breadcrumbs = useMemo(
    () =>
      ruleEditorTeammate &&
      buildRuleEditorBreadcrumbsForOtherTeammate(namespace, ruleEditorTeammate, navigateToLink),
    [namespace, ruleEditorTeammate, navigateToLink],
  );

  if (!ruleEditorTeammate || !breadcrumbs) {
    return null;
  }

  return (
    <SettingsBannersProvider>
      <RuleEditorServiceContext.Provider value={editorServiceContext}>
        <AsyncDataLoader data={ruleIntegrationsAsyncData}>
          {(ruleIntegration) => (
            <RuleEditorWrapper
              ruleIntegration={ruleIntegration}
              panelId={panelId}
              resourcePage={subResourcePage}
              namespace={ruleEditorTeammate.namespace}
              ruleId={ruleId}
              breadcrumbs={breadcrumbs}
            />
          )}
        </AsyncDataLoader>
      </RuleEditorServiceContext.Provider>
    </SettingsBannersProvider>
  );
};
